import { useCallback, useMemo, useState } from "react";
import {
  CUP_NAME_REGEX,
  MODE_FIELD_DEFAULTS,
  MODE_KEYS,
} from "../../Components/Device/Tabs/SettingsTab/storageRoles/brewingSettings/cupConstants";

function makeDefaultModes() {
  return MODE_KEYS.reduce((acc, key) => {
    acc[key] = { ...MODE_FIELD_DEFAULTS };
    return acc;
  }, {});
}

function generateCupId(name) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return `cup_${slug}_${Date.now().toString(36)}`;
}

export function useTurkishCoffeeCups(initialCups) {
  const [cups, setCups] = useState(() => initialCups || []);
  const [selectedCupId, setSelectedCupId] = useState(
    () => (initialCups?.[0]?.id) || null
  );
  const [selectedMode, setSelectedMode] = useState("normal");
  const [dirty, setDirty] = useState(false);

  const selectedCup = useMemo(
    () => cups.find((c) => c.id === selectedCupId) || null,
    [cups, selectedCupId]
  );

  const setCupField = useCallback((cupId, field, value) => {
    setCups((prev) =>
      prev.map((c) => (c.id === cupId ? { ...c, [field]: value } : c))
    );
    setDirty(true);
  }, []);

  const setModeField = useCallback((cupId, mode, field, value) => {
    setCups((prev) =>
      prev.map((c) =>
        c.id !== cupId
          ? c
          : {
              ...c,
              modes: {
                ...c.modes,
                [mode]: { ...c.modes[mode], [field]: value },
              },
            }
      )
    );
    setDirty(true);
  }, []);

  const addCup = useCallback(
    (name) => {
      const trimmed = (name || "").trim();
      if (!CUP_NAME_REGEX.test(trimmed))
        return { ok: false, error: "Tek kelime, max 7 karakter" };
      if (cups.some((c) => c.name === trimmed))
        return { ok: false, error: "Bu isim zaten var" };
      const id = generateCupId(trimmed);
      const newCup = {
        id,
        name: trimmed,
        order: cups.length,
        isSystem: false,
        waterLevel: 50,
        modes: makeDefaultModes(),
      };
      setCups((prev) => [...prev, newCup]);
      setSelectedCupId(id);
      setDirty(true);
      return { ok: true, id };
    },
    [cups]
  );

  const removeCup = useCallback(
    (cupId) => {
      const cup = cups.find((c) => c.id === cupId);
      if (!cup || cup.isSystem)
        return { ok: false, error: "Sistem fincanı silinemez" };
      setCups((prev) =>
        prev
          .filter((c) => c.id !== cupId)
          .map((c, i) => ({ ...c, order: i }))
      );
      if (selectedCupId === cupId) setSelectedCupId(cups[0]?.id || null);
      setDirty(true);
      return { ok: true };
    },
    [cups, selectedCupId]
  );

  const renameCup = useCallback(
    (cupId, name) => {
      const trimmed = (name || "").trim();
      if (!CUP_NAME_REGEX.test(trimmed))
        return { ok: false, error: "Tek kelime, max 7 karakter" };
      if (cups.some((c) => c.id !== cupId && c.name === trimmed))
        return { ok: false, error: "Bu isim zaten var" };
      setCups((prev) =>
        prev.map((c) => (c.id === cupId ? { ...c, name: trimmed } : c))
      );
      setDirty(true);
      return { ok: true };
    },
    [cups]
  );

  const reorderCups = useCallback((orderedIds) => {
    setCups((prev) => {
      const map = new Map(prev.map((c) => [c.id, c]));
      return orderedIds.map((id, i) => ({ ...map.get(id), order: i }));
    });
    setDirty(true);
  }, []);

  const reset = useCallback((next) => {
    setCups(next || []);
    setSelectedCupId(next?.[0]?.id || null);
    setSelectedMode("normal");
    setDirty(false);
  }, []);

  return {
    cups,
    selectedCup,
    selectedCupId,
    setSelectedCupId,
    selectedMode,
    setSelectedMode,
    dirty,
    setCupField,
    setModeField,
    addCup,
    removeCup,
    renameCup,
    reorderCups,
    reset,
  };
}
